import { Show, Switch, Match, For, createMemo, type Accessor, type JSX } from 'solid-js';
import type {
  LlamacppSnapshot,
  GpuSnapshot,
  LlamacppServerView,
  RouterModelView,
  LlamacppInventoryView,
} from '../state/metrics';
import { serverKeyOf } from '../state/metrics';
import { selectedServerKey, setSelectedServerKey } from '../state/selection';
import { llamacppVramMb, placement } from './llamacpp-vram-derive';
import { HudPanel } from '../ui/hud-panel';
import { InfoTip } from '../ui/info-tip';
import { HudTabs } from '../ui/hud-tabs';

/** Plain-language tooltip copy. */
const TIPS = {
  modalities:
    'Extra input types this build accepts besides text, such as images. A text-only build shows no badge here.',
  vram:
    "Video memory the llama.cpp process is using on the GPU (from the NVIDIA driver). llama.cpp doesn't report how many layers are on the GPU vs the CPU, so placement is inferred by comparing this to the model file size: if less than the model's size is in VRAM, part of it runs on the CPU.",
  quant:
    "How much the model's weights are compressed. 'Q4_K - Medium' is a 4-bit medium-quality quantization — smaller and faster than the original 16-bit weights, at a small quality cost. Lower bits (Q3, Q2) fit in less memory but degrade quality.",
  tools:
    'Whether this model can call tools (function calling) — read files, run commands, edit code. Agentic clients like opencode need this; a model without tool support can only chat.',
  inventory:
    'GGUF files found in the directories llama.cpp itself has used (its --models-dir, the folder of its --model, its download cache). Three heuristics keep non-models out: tokenizer test fixtures (ggml-vocab-*), vision projectors (*mmproj*) and anything under 20 MB. Sharded models count once. Only a running server can say whether a model is loaded — nothing else is claimed.',
} as const;

/** A HUD key/value line: small-caps label left, value content right. */
function Row(props: { label: JSX.Element; children: JSX.Element }) {
  return (
    <div class="flex items-center justify-between gap-2.5 border-t border-line/40 py-1.5 first:border-t-0 text-[0.83rem]">
      <span class="hud-mono inline-flex items-center gap-1 text-[0.625rem] uppercase tracking-[0.18em] text-faint">
        {props.label}
      </span>
      {props.children}
    </div>
  );
}

/**
 * True once there are at least two servers to choose between. The single shared
 * threshold behind three separate decisions — the tab strip's visibility
 * (`ServerTabs`), the card's per-server override (`activeServer` below), and the
 * cockpit's server chip (`model-hero.tsx`, via `activeServerOf`) — kept as one predicate so a
 * future edit to one can't silently desync it from the other two, which is the
 * same shape of bug as the two fallback gaps fixed alongside this.
 */
export function hasServerChoice(servers: LlamacppServerView[] | null | undefined): boolean {
  return (servers?.length ?? 0) >= 2;
}

/**
 * The identity of one server for selection and tab keys. NOT the bare port: two
 * servers can listen on the same port on different interfaces, and `HudTabs` keys
 * on `id` — two tabs sharing an id both read `aria-selected="true"`, and clicking
 * the second shows the first's rows. `host:port` is unique where a port alone is
 * not, and (unlike an index) it stays the same server across polls as others come
 * and go.
 */
export function serverKey(s: LlamacppServerView): string {
  return serverKeyOf(s.host, s.port);
}

/**
 * Tab label: the port alone reads best, but when another server shares that port
 * the port alone would show two identical tabs — so the host joins it, which is
 * exactly what distinguishes them.
 */
export function serverLabel(s: LlamacppServerView, all: LlamacppServerView[]): string {
  const shared = all.filter((o) => o.port === s.port).length > 1;
  return shared ? `${s.host}:${s.port}` : `:${s.port}`;
}

/**
 * The server a per-server view should describe: the chosen one while it still
 * exists in the list, else the first — a vanished selection must not point into
 * the void. `null` with fewer than two servers: then there is no tab strip and no
 * choice, and the flat fields (which mirror that one server) are the truth.
 * Shared by the card and the cockpit hero so the two cannot disagree.
 */
export function activeServerOf(
  servers: LlamacppServerView[] | null | undefined,
  selectedKey: string | null,
): LlamacppServerView | null {
  if (!hasServerChoice(servers)) return null;
  const list = servers!;
  return list.find((s) => serverKey(s) === selectedKey) ?? list[0]!;
}

/**
 * Heading for the on-disk list; the two empty states are different facts. When the
 * agent cut the list to fit the wire, `total` is what it found: say "K of N" — and
 * before the empty-list branch, so a list cut to nothing is "showing 0 of N", never
 * "no models" about a disk that has them.
 */
export function inventoryHeading(inv: LlamacppInventoryView): string {
  if (inv.roots.length === 0) return 'no model directories known yet';
  const n = inv.models.length;
  const floor = inv.roots.some((r) => r.truncated) ? 'at least ' : '';
  if (inv.total != null && inv.total > n) return `showing ${n} of ${floor}${inv.total} models on disk`;
  if (n === 0) return 'no models in the known directories';
  return `${floor}${n} ${n === 1 ? 'model' : 'models'} on disk`;
}

/** Summary of a router catalogue: "K of N" when the agent cut `omitted` entries to fit the wire. */
export function catalogueHeading(shown: number, omitted: number | null): string {
  return omitted != null && omitted > 0
    ? `${shown} of ${shown + omitted} models in directory`
    : `${shown} models in directory`;
}

/** `HH:MM:SS` of a unix time, in the viewer's zone. */
export function formatObserved(unixSecs: number): string {
  return new Date(unixSecs * 1000).toLocaleTimeString('en-GB', { hour12: false });
}

function gb(sizeMb: number | null): string {
  return sizeMb != null ? `${(sizeMb / 1024).toFixed(1)} GB` : '—';
}

/**
 * Models on disk, machine-wide. Distinct from the router's own directory list
 * below it (a fact about ONE server): this one comes from walking every root any
 * server has named, and it survives every server being stopped.
 */
export function InventoryList(props: { inventory: LlamacppInventoryView | null }) {
  const inv = () => props.inventory;
  return (
    <Show when={inv()}>
      {(i) => (
        <div data-testid="llamacpp-inventory" class="mt-2 border-t border-line pt-1">
          <div class="hud-mono flex items-center gap-1 py-2 text-[0.6563rem] uppercase tracking-[0.14em] text-holo opacity-85">
            {inventoryHeading(i())} <InfoTip text={TIPS.inventory} />
          </div>
          <For each={i().models}>
            {(m) => (
              <div
                data-testid="inventory-model"
                class="flex items-center justify-between gap-2.5 border-b border-line/40 py-2 text-xs last:border-b-0"
                title={m.path}
              >
                <span class={m.loaded === true ? 'font-semibold' : 'text-muted'}>{m.name ?? m.id}</span>
                <span class="tabular-nums text-faint">
                  {gb(m.sizeMb)}
                  <Show when={m.loaded === true}> · loaded</Show>
                  <Show when={m.loaded === false}> · idle</Show>
                </span>
              </div>
            )}
          </For>
          <Show when={i().roots.length > 0}>
            <div class="mt-1 flex flex-col gap-0.5 text-[0.71rem] text-faint">
              <For each={i().roots}>
                {(r) => (
                  <span class="truncate font-mono" title={r.path}>
                    {r.path} ·{' '}
                    {!r.readable ? 'unreadable' : `${r.truncated ? '≥ ' : ''}${r.modelCount} ${r.modelCount === 1 ? 'model' : 'models'}`}
                  </span>
                )}
              </For>
            </div>
          </Show>
        </div>
      )}
    </Show>
  );
}

/**
 * Tab strip for the llama.cpp servers this machine's agent has found, one tab per
 * server. A single server needs no switcher — one tab would be chrome for a choice
 * that doesn't exist — and `servers === null` (no `servers` key on the wire) must
 * render the same nothing, not "zero servers".
 *
 * The emptiness check lives inside a `<Show>` rather than a top-level early
 * `return null`: a Solid component body runs once, so a plain `if` at the top
 * would freeze the "more than one server" verdict at first mount and never
 * revisit it as servers come and go between polls — `<Show>` re-evaluates its
 * `when` on every change instead, the same pattern `GpuCluster`'s device tabs use.
 */
export function ServerTabs(props: {
  servers: LlamacppServerView[] | null;
  selected: string | null;
  onSelect: (key: string) => void;
}) {
  const list = () => props.servers;
  const showTabs = () => hasServerChoice(list());
  /**
   * The server to draw as active. Defends the same "a vanished selection must not
   * point into the void" rule `LlamacppCard`'s `activeServer` applies, but at this
   * component's own boundary: a caller could pass `null` (nothing chosen yet) or a
   * key that no longer appears in `servers` (it was live in an earlier poll and
   * has since disappeared) — both fall back to the first server rather than
   * leaving no tab marked selected.
   */
  const resolvedKey = () => {
    const l = list()!;
    const sel = props.selected;
    return sel != null && l.some((s) => serverKey(s) === sel) ? sel : serverKey(l[0]!);
  };
  return (
    <Show when={showTabs()}>
      <HudTabs
        label="llama.cpp servers"
        selected={resolvedKey()}
        items={list()!.map((s) => ({
          id: serverKey(s),
          label: serverLabel(s, list()!),
          detail: s.model ?? undefined,
          live: s.detected,
        }))}
        onSelect={props.onSelect}
      />
    </Show>
  );
}

/**
 * Empty-state copy for "no llama.cpp server here": never claim absence where the
 * agent could not look. `discoverySupported === false` means the platform has no
 * `/proc` to scan (e.g. macOS today) — auto-detect never ran, so the honest claim
 * is about the platform, not the server. `true`, or `null` (an agent too old to
 * say), both mean a real search came up empty — a real absence.
 */
export function emptyStateCopy(discoverySupported: boolean | null): string {
  if (discoverySupported === false) {
    return 'Auto-detect is not available on this platform — polling the configured address only.';
  }
  return 'No llama.cpp server detected.';
}

/**
 * Every fact this card renders about ONE server, read from one place so no row can
 * quietly describe a different server than the one whose tab is selected. Before
 * this existed the card mixed the two: Model, size and VRAM followed the tab while
 * the sleeping badge, modalities, quant, tools, the router badge, the `--metrics`
 * hint and the directory list all kept showing the primary.
 */
export type CardServer = {
  model: string | null;
  modelSizeMb: number | null;
  quant: string | null;
  supportsTools: boolean | null;
  supportsParallelTools: boolean | null;
  isSleeping: boolean | null;
  modalities: string[] | null;
  metricsEnabled: boolean;
  router: boolean | null;
  modelsLoaded: number | null;
  modelsMax: number | null;
  models: RouterModelView[] | null;
  modelsOmitted: number | null;
  version: string | null;
  /** The PID to attribute GPU memory to: the process actually holding the model
   *  (the router's child in router mode), not necessarily the listener. */
  vramPid: number | null;
  /** True when this describes a chosen entry from `servers[]` rather than the flat
   *  fields — the flat fields are the only ones an old agent sends, and the only
   *  ones where a name-match VRAM fallback is safe (there is exactly one server). */
  perServer: boolean;
};

/**
 * The server the card describes: the selected entry when there is a choice to
 * make, else the flat fields — which mirror the primary and are all an agent
 * predating discovery sends. `active` is expected to be `null` unless a tab strip
 * is actually up (see `hasServerChoice`).
 */
export function cardServer(
  snap: LlamacppSnapshot | null,
  active: LlamacppServerView | null,
): CardServer {
  if (active) {
    return {
      model: active.model,
      modelSizeMb: active.modelSizeMb,
      quant: active.quant,
      supportsTools: active.supportsTools,
      supportsParallelTools: active.supportsParallelTools,
      isSleeping: active.isSleeping,
      modalities: active.modalities,
      metricsEnabled: active.metricsEnabled,
      router: active.router,
      modelsLoaded: active.modelsLoaded,
      modelsMax: active.modelsMax,
      models: active.models,
      modelsOmitted: active.modelsOmitted,
      version: active.version,
      vramPid: active.serverPid ?? active.pid,
      perServer: true,
    };
  }
  return {
    model: snap?.model ?? null,
    modelSizeMb: snap?.modelSizeMb ?? null,
    quant: snap?.quant ?? null,
    supportsTools: snap?.supportsTools ?? null,
    supportsParallelTools: snap?.supportsParallelTools ?? null,
    isSleeping: snap?.isSleeping ?? null,
    modalities: snap?.modalities ?? null,
    metricsEnabled: snap?.metricsEnabled ?? false,
    router: snap?.router ?? null,
    modelsLoaded: snap?.modelsLoaded ?? null,
    modelsMax: snap?.modelsMax ?? null,
    models: snap?.models ?? null,
    modelsOmitted: snap?.modelsOmitted ?? null,
    version: snap?.version ?? null,
    vramPid: snap?.serverPid ?? null,
    perServer: false,
  };
}

/** llama.cpp runner card. Rendered only when a llama.cpp server is detected. Live inference
 * numbers (speed, context, read/write, avg, queue, processed, sparkline) live in the cockpit
 * hero now — this card keeps the runner's own facts (mode, VRAM, capabilities, directory). */
export function LlamacppCard(props: {
  data: Accessor<LlamacppSnapshot | null>;
  gpu?: Accessor<GpuSnapshot | null>;
}) {
  const snap = () => props.data();

  // Selection for the server tabs: the chosen server while it still exists in the
  // latest list, else the first one — a vanished selection (servers come and go
  // between polls) must not leave the tab strip pointing at nothing. Keyed by
  // `host:port` rather than the bare port, which two servers can share. The
  // signal itself is shared state (`state/selection.ts`) so the cockpit hero
  // follows the same tab this card has selected.
  //
  // Gated on `hasServerChoice`, not just a non-empty list: with fewer than two
  // servers there is no tab strip to pick from (see `ServerTabs`), so the rows
  // below read the flat fields, which mirror that one server anyway and are all an
  // agent predating discovery sends.
  const activeServer = createMemo<LlamacppServerView | null>(() =>
    activeServerOf(snap()?.servers, selectedServerKey()),
  );
  // THE one source every row below reads. Routing each row through this is the
  // whole point: a card that mixes the selected server's model with the primary's
  // quant, badges and hints describes two servers under a tab strip that promises
  // one — and the sleeping/placement pair does worse than confuse, it prints a
  // claim the data contradicts.
  const view = createMemo<CardServer>(() => cardServer(snap(), activeServer()));
  /** The selected server's key, as a named accessor: an inline ternary in a JSX
   *  prop compiles to a `memo()` that a click handler would create with no owner
   *  ("computations created outside a createRoot will never be disposed"). */
  const activeKey = () => {
    const active = activeServer();
    return active ? serverKey(active) : null;
  };
  const detected = () => snap()?.detected ?? false;
  const inventory = () => snap()?.inventory ?? null;
  /** Nothing is live, but the disk walk from an earlier poll is still worth
   *  showing — a remembered fact beats no fact, as long as it's labeled as such. */
  const lastKnown = () => !detected() && (inventory()?.roots.length ?? 0) > 0;
  const stale = () => snap()?.stale ?? false;
  const metricsOn = () => view().metricsEnabled;
  const isRouter = () => view().router === true;
  /** `router · N of M loaded` — degrades to `· N loaded`, then to bare `router`. */
  const routerBadge = () => {
    if (!isRouter()) return null;
    const loaded = view().modelsLoaded;
    if (loaded == null) return 'router';
    const max = view().modelsMax;
    return max == null ? `router · ${loaded} loaded` : `router · ${loaded} of ${max} loaded`;
  };
  // A router with no model to report cannot tell us anything about --metrics:
  // the flag lives on the per-model child server. Saying "restart with
  // --metrics" there would be a lie (the live server has it on).
  const showMetricsHint = () => !metricsOn() && !(isRouter() && view().model == null);
  const routerNote = () =>
    view().modelsLoaded === 0
      ? 'No model loaded — the router starts one on the first request.'
      : "Router is up, but the model's details are unavailable right now.";
  const modelName = () => view().model ?? '—';
  const modelSizeMb = () => view().modelSizeMb;
  // A null PID means the process holding the model was never resolved (a router
  // child that died between /v1/models and the /proc scan, an unreadable /proc, a
  // remote target) — NOT "no PID known, so match by name". The name-match fallback
  // inside llamacppVramMb sums every process called `llama-server` on the box: with
  // a router it would credit Ollama's identically-named runner to this card, and
  // with several servers it would credit every one of them to whichever tab is
  // open. It stays available only for the flat, single-server case it was written
  // for; everywhere else an honest `null` beats a plausible guess.
  const vramMb = () => {
    const v = view();
    if (v.vramPid == null && (v.perServer || v.router === true)) return null;
    return llamacppVramMb(props.gpu?.() ?? null, v.vramPid);
  };
  const place = () => placement(vramMb(), modelSizeMb(), view().isSleeping);
  const vramGb = () => `${(vramMb()! / 1024).toFixed(1)} GB`;
  /** `version · router`, degrading to just one of the two — never a dangling
   * " · router" (or trailing " · ") when the other half is unknown. In
   * last-known mode neither is known (no server answered), so it's just the
   * plain label instead. */
  const sub = () => {
    if (lastKnown()) return 'last known';
    const parts: string[] = [];
    const version = view().version;
    if (version) parts.push(version);
    if (isRouter()) parts.push('router');
    return parts.length ? parts.join(' · ') : undefined;
  };

  return (
    <Show when={detected() || lastKnown()}>
      <HudPanel title="llama.cpp" sub={sub()}>
        <Show when={detected()}>
          <ServerTabs
            servers={snap()?.servers ?? null}
            selected={activeKey()}
            onSelect={setSelectedServerKey}
          />
          <div class="mb-3 flex flex-wrap items-center gap-2 gap-y-1">
            <span class="inline-flex h-[13px] w-[13px] flex-none items-center justify-center rounded-full text-good bg-good-soft">
              <span class="h-1.5 w-1.5 rounded-full bg-current" />
            </span>
            <Show when={view().isSleeping === true}>
              <span class="rounded-full bg-surface-2 px-2 py-0.5 text-[0.62rem] font-semibold uppercase text-warn">
                sleeping
              </span>
            </Show>
            <For each={view().modalities ?? []}>
              {(m) => (
                <span class="rounded-full bg-surface-2 px-2 py-0.5 text-[0.62rem] font-semibold uppercase text-good">
                  {m}
                </span>
              )}
            </For>
            <Show when={(view().modalities?.length ?? 0) > 0}>
              <InfoTip text={TIPS.modalities} />
            </Show>
            <Show when={stale()}>
              <span class="inline-flex items-center gap-1 text-[0.71rem] text-faint">
                <span class="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                refreshing…
              </span>
            </Show>
            <Show when={routerBadge()}>
              {(b) => (
                <span class="rounded-full border border-border bg-surface-2 px-2 py-0.5 text-[0.71rem] font-semibold text-muted">
                  {b()}
                </span>
              )}
            </Show>
          </div>

          <div class="flex flex-col">
            <Row label="Model">
              <span class="font-mono tabular-nums text-ink">{modelName()}</span>
            </Row>

            <Show when={view().quant}>
              {(q) => (
                <Row label={<>Quant <InfoTip text={TIPS.quant} /></>}>
                  <span class="font-mono tabular-nums text-ink">{q()}</span>
                </Row>
              )}
            </Show>

            <Show when={view().supportsTools != null}>
              <Row label={<>Tools <InfoTip text={TIPS.tools} /></>}>
                <Show
                  when={view().supportsTools}
                  fallback={<span class="font-mono text-faint">✕ not supported</span>}
                >
                  <span class="font-mono text-good">
                    ✓ supported<Show when={view().supportsParallelTools}> · parallel</Show>
                  </span>
                </Show>
              </Row>
            </Show>

            <Row label={<>VRAM <InfoTip text={TIPS.vram} /></>}>
              <Switch fallback={<span class="font-mono tabular-nums text-faint">—</span>}>
                <Match when={place() === 'gpu'}>
                  <span class="font-mono tabular-nums text-ink">
                    {vramGb()} <span class="text-good">· all on GPU</span>
                  </span>
                </Match>
                <Match when={place() === 'partial'}>
                  <span class="font-mono tabular-nums text-ink">
                    {vramGb()} <span class="text-warn">· partly on CPU</span>
                  </span>
                </Match>
                <Match when={place() === 'vram-only'}>
                  <span class="font-mono tabular-nums text-ink">{vramGb()}</span>
                </Match>
              </Switch>
            </Row>

            {/* Live inference numbers (speed, context, read/write, avg, queue, processed) moved
                to the cockpit hero. A router with no model can't tell us anything about
                --metrics (the flag lives on the per-model child server), so it gets its own
                honest note instead of the --metrics hint. */}
            <Show when={!metricsOn()}>
              <Show
                when={showMetricsHint()}
                fallback={<p class="border-t border-line/40 py-1.5 text-[0.83rem] text-muted">{routerNote()}</p>}
              >
                <p class="border-t border-line/40 py-1.5 text-[0.83rem] text-muted">
                  Inference metrics are off. Restart llama-server with{' '}
                  <code class="rounded bg-surface-2 px-1 font-mono text-ink">--metrics</code> to see
                  tokens/sec, queue depth, and context fill.
                </p>
              </Show>
            </Show>
          </div>

          <Show when={(view().models?.length ?? 0) > 0 || (view().modelsOmitted ?? 0) > 0}>
            <details class="mt-2 border-t border-line pt-1">
              <summary class="hud-mono cursor-pointer list-none py-2 text-[0.6563rem] uppercase tracking-[0.14em] text-holo opacity-85">
                {catalogueHeading(view().models?.length ?? 0, view().modelsOmitted)}
              </summary>
              <For each={view().models ?? []}>
                {(m) => (
                  <div class="flex items-center justify-between gap-2.5 border-b border-line/40 py-2 text-xs last:border-b-0">
                    <span class={m.loaded ? 'font-semibold' : 'text-muted'}>{m.id}</span>
                    <span class="tabular-nums text-faint">
                      {m.sizeMb != null ? `${(m.sizeMb / 1024).toFixed(1)} GB · ` : ''}
                      {m.loaded ? 'loaded' : 'idle'}
                    </span>
                  </div>
                )}
              </For>
              <Show when={(view().modelsOmitted ?? 0) > 0}>
                <div data-testid="catalogue-more" class="py-2 text-xs text-muted">
                  +{view().modelsOmitted} more
                </div>
              </Show>
            </details>
          </Show>
        </Show>

        <Show when={lastKnown()}>
          <p class="py-1.5 text-[0.83rem] text-muted">
            No llama.cpp server is running. These are the models found in the directories it
            used before — read at {formatObserved(inventory()!.observedAt)}.
          </p>
        </Show>
        <InventoryList inventory={inventory()} />
      </HudPanel>
    </Show>
  );
}
